import { Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DocumentMetaGrid, DocumentSection, formatMoney } from '@/components/app-document-detail';

export interface Declaracion {
  formulario: number;
  periodo: number;
  numeroDocumento?: string;
  fechaPresentacion?: string | null; // dd/MM/yyyy (histórico completo)
  estado?: string | null;
}
export interface Obligacion {
  codigo: number;
  nombre: string;
  desde: string | null;
  hasta: string | null;
}
export interface ActividadEconomica {
  codigo: string;
  nombre: string;
  principal: boolean;
  desde: string | null;
}
export interface CuentaCorrienteFila {
  obligacion: string;
  tipoCuenta: string;
  valorMulta: number;
  valorMora: number;
  totalDeuda: number;
  totalCredito: number;
}
export interface Snapshot {
  status: string;
  source: string;
  scrapedAt: string | null;
  estado: string | null;
  saldoFavor: string | null;
  perfil: Record<string, unknown> | null;
  ultimasDeclaraciones: Declaracion[] | null;
  obligaciones: Obligacion[] | null;
  actividadesEconomicas: ActividadEconomica[] | null;
  cuentaCorrienteDetalle: CuentaCorrienteFila[] | null;
  error: string | null;
}

function fmtPeriodo(p: number): string {
  const s = String(p);
  return s.length === 6 ? `${s.slice(4)}/${s.slice(0, 4)}` : s;
}
function get(obj: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>((o, k) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined), obj);
}
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

/**
 * Render read-only del snapshot de Marangatu (saldo, estado, cuenta corriente,
 * obligaciones, actividades económicas, perfil, últimas presentaciones).
 * Compartido entre el card del admin y el del tenant — la única diferencia es el
 * chrome (header, botones de sync/manual) que pone cada consumidor.
 */
export function MarangatuSnapshotView({
  snapshot,
  showSaldo = true,
}: {
  snapshot: Snapshot;
  /** Saldo a favor — se oculta en el tenant (vive en Presentaciones de IVA). */
  showSaldo?: boolean;
}): React.ReactElement {
  const perfil = snapshot.perfil ?? null;
  const oblig = snapshot.obligaciones ?? [];
  const cc = snapshot.cuentaCorrienteDetalle ?? [];
  const ccPendiente = cc.filter(
    (r) => r.totalDeuda > 0 || r.totalCredito > 0 || r.valorMulta > 0 || r.valorMora > 0
  );

  const estadoTile = (
    <div className="rounded-lg border border-border bg-muted/30 p-4">
      <div className="text-xs text-muted-foreground">Estado del contribuyente</div>
      <div className="mt-1">
        <Badge variant={snapshot.estado === 'ACTIVO' ? 'success' : 'secondary'}>
          {snapshot.estado ?? '—'}
        </Badge>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {showSaldo ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <div className="text-xs text-muted-foreground">Saldo a favor</div>
            <div className="text-2xl font-bold tabular-nums">
              {snapshot.saldoFavor != null ? formatMoney(snapshot.saldoFavor) : '—'}
            </div>
          </div>
          {estadoTile}
        </div>
      ) : (
        estadoTile
      )}

      {cc.length > 0 && (
        <DocumentSection title="Cuenta corriente">
          {ccPendiente.length === 0 ? (
            <p className="flex items-center gap-1.5 text-sm text-success">
              <Check className="size-4" /> Sin deuda ni multas pendientes.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {ccPendiente.map((r, i) => (
                <li key={i} className="px-3 py-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{r.obligacion}</span>
                    <span className="tabular-nums text-destructive">
                      {r.totalDeuda > 0 ? formatMoney(r.totalDeuda) : formatMoney(r.totalCredito)}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {r.tipoCuenta}
                    {r.valorMulta > 0 ? ` · multa ${formatMoney(r.valorMulta)}` : ''}
                    {r.valorMora > 0 ? ` · mora ${formatMoney(r.valorMora)}` : ''}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DocumentSection>
      )}

      {oblig.length > 0 && (
        <DocumentSection title="Obligaciones activas">
          <ul className="divide-y divide-border rounded-lg border border-border">
            {oblig.map((o, i) => (
              <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="font-medium">
                  {o.nombre} <span className="font-mono text-xs text-muted-foreground">({o.codigo})</span>
                </span>
                <span className="text-xs text-muted-foreground">desde {o.desde ?? '—'}</span>
              </li>
            ))}
          </ul>
        </DocumentSection>
      )}

      {/* "Actividades económicas" se removió: la actividad principal vive en el
          Perfil fiscal (tenant_rucs). Este view es solo ESTADO DE CUENTA SET.
          "Perfil" (nombre/correo/categoría/domicilio/inicio/nombre fantasía) y
          "Últimas presentaciones" se removieron de acá: el perfil fiscal vive en
          UN solo lugar (columnas de tenant_rucs, que el scrape llena — ver
          marangatu-sync `updateTenantRucFromSnapshot`), y el histórico de
          presentaciones se muestra completo en las páginas de IVA / Renta. Este
          view queda con el ESTADO DE CUENTA SET (estado, saldo, cuenta corriente,
          obligaciones, actividades) que no se duplica en otro lado. */}
      {perfil && (get(perfil, 'generales.categoriaContribuyente') || get(perfil, 'resumen.tipoContribuyente')) ? (
        <DocumentSection title="Clasificación SET">
          <DocumentMetaGrid
            items={[
              { label: 'Categoría', value: str(get(perfil, 'generales.categoriaContribuyente')) },
              { label: 'Tipo', value: str(get(perfil, 'resumen.tipoContribuyente')) },
            ]}
          />
        </DocumentSection>
      ) : null}
    </div>
  );
}

/** Filtra las presentaciones del snapshot por número de formulario. */
export function filterPresentaciones(
  decls: Declaracion[] | null | undefined,
  formularios: number[]
): Declaracion[] {
  if (!Array.isArray(decls)) return [];
  return decls.filter((d) => formularios.includes(d.formulario));
}

/** dd/MM/yyyy desde un período AAAAMM (o el valor crudo). Reexport para reuso. */
export function formatPeriodo(p: number): string {
  return fmtPeriodo(p);
}
